import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router";
import { LoadingScreen } from "../../components/LoadingScreen";
import {
  ApiError,
  customerApi,
  requestAccountDeletion,
  type CustomerBooking,
  type CustomerFoodOrder,
  type CustomerSession,
} from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { LegalPage } from "./LegalPage";

const ACTIVE_BOOKING_STATUSES = ["pending", "confirmed", "checked_in"];

function formatDateTime(value: string) {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function ActiveItemList({
  bookings,
  sessions,
  foodOrders,
}: {
  bookings: CustomerBooking[];
  sessions: CustomerSession[];
  foodOrders: CustomerFoodOrder[];
}) {
  return (
    <div className="rounded-xl border border-amber-400/20 bg-amber-400/10 p-4 text-amber-100">
      <p className="font-semibold">
        You cannot request account deletion while the following items are still active:
      </p>
      <ul className="mt-2 list-disc space-y-1 pl-5 text-sm">
        {bookings.map((booking) => (
          <li key={booking.id}>
            {booking.status} booking at {booking.venue_name} on{" "}
            {formatDateTime(booking.scheduled_start_at)}
          </li>
        ))}
        {sessions.map((session) => (
          <li key={session.id}>
            Active session at {session.venue_name} started{" "}
            {formatDateTime(session.started_at)}
          </li>
        ))}
        {foodOrders.map((order) => (
          <li key={order.id}>
            Pending food order ({order.items.map((item) => item.item_name).join(", ")})
          </li>
        ))}
      </ul>
      <p className="mt-2 text-sm">
        Please cancel or complete these items first, then return here.
      </p>
    </div>
  );
}

export function AccountDeletionPage() {
  const { signOut, status, user } = useAuth();
  const actionPending = useRef(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [signedOut, setSignedOut] = useState(false);
  const [signOutError, setSignOutError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [bookings, setBookings] = useState<CustomerBooking[]>([]);
  const [sessions, setSessions] = useState<CustomerSession[]>([]);
  const [foodOrders, setFoodOrders] = useState<CustomerFoodOrder[]>([]);
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const isCustomer = user?.role === "customer";

  useEffect(() => {
    if (status !== "authenticated" || !isCustomer) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    async function load() {
      setIsLoading(true);
      setLoadError(null);
      try {
        const [bookingsResponse, sessionsResponse] = await Promise.all([
          customerApi.listBookings(),
          customerApi.listActiveSessions(),
        ]);

        const activeBookings = bookingsResponse.bookings.filter((booking) =>
          ACTIVE_BOOKING_STATUSES.includes(booking.status),
        );

        const activeSessions = sessionsResponse.sessions.filter(
          (session) => session.status === "active",
        );

        const orderResponses = await Promise.all(
          activeSessions.map((session) =>
            customerApi.listSessionFoodOrders(session.id),
          ),
        );

        const pendingOrders = orderResponses
          .flatMap((response) => response.orders)
          .filter((order) => order.status === "pending");

        if (!cancelled) {
          setBookings(activeBookings);
          setSessions(activeSessions);
          setFoodOrders(pendingOrders);
        }
      } catch (requestError) {
        if (!cancelled) {
          setLoadError(
            requestError instanceof ApiError
              ? requestError.detail
              : "Unable to check your active bookings and sessions.",
          );
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    void load();

    return () => {
      cancelled = true;
    };
  }, [status, isCustomer, user?.id, loadAttempt]);

  const hasActiveItems = useMemo(
    () => bookings.length > 0 || sessions.length > 0 || foodOrders.length > 0,
    [bookings, sessions, foodOrders],
  );

  const handleSubmit = async () => {
    if (actionPending.current || !confirmed || hasActiveItems || isLoading || loadError || !isCustomer) return;
    actionPending.current = true;

    setSubmitting(true);
    setSubmitError(null);

    try {
      await requestAccountDeletion({
        reason: reason.trim() || undefined,
      });
      setSubmitted(true);
      try {
        await signOut();
        setSignedOut(true);
      } catch {
        setSignOutError(true);
      }
    } catch (requestError) {
      setSubmitError(
        requestError instanceof ApiError
          ? requestError.detail
          : "Unable to submit account deletion request.",
      );
    } finally {
      actionPending.current = false;
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <LegalPage
        eyebrow="Account"
        lastUpdated="July 2026"
        title="Delete your Gaamed account"
      >
        <div className="rounded-xl border border-emerald-400/20 bg-emerald-400/10 p-4 text-[var(--color-text)]" role="status">
          <p className="font-semibold">Your deletion request has been received.</p>
          <p className="mt-2 text-sm">
            We will review your request and email you when it has been processed.
            {signedOut ? " You are now signed out of Gaamed." : submitting ? " Signing you out..." : ""}
          </p>
        </div>
        {signOutError && <div role="alert">
          <p>Your request was received, but we could not sign you out. Please retry.</p>
          <button className="ops-button" disabled={submitting} onClick={async () => {
            setSubmitting(true);
            try { await signOut(); setSignedOut(true); setSignOutError(false); }
            catch { setSignOutError(true); }
            finally { setSubmitting(false); }
          }} type="button">Retry sign out</button>
        </div>}
        <Link className="ops-button" data-variant="secondary" to="/">Back to home</Link>
      </LegalPage>
    );
  }

  if (status === "loading") {
    return <LoadingScreen />;
  }

  if (status === "anonymous") {
    return (
      <LegalPage
        attribution={
          <>
            For questions email{" "}
            <a href="mailto:privacy@gaamed.com">privacy@gaamed.com</a>
          </>
        }
        eyebrow="Account"
        lastUpdated="July 2026"
        title="Delete your Gaamed account"
      >
        <p>
          You have the right to request deletion of your Gaamed account and the personal data
          associated with it. We will review your request, confirm there are no active bookings or
          sessions, and permanently delete your account.
        </p>
        <p>
          <Link
            className="ops-button"
            data-variant="primary"
            to="/sign-in?returnTo=/legal/account-deletion"
          >
            Sign in to request deletion
          </Link>
        </p>
      </LegalPage>
    );
  }

  if (!isCustomer) {
    return (
      <LegalPage
        eyebrow="Account"
        lastUpdated="July 2026"
        title="Delete your Gaamed account"
      >
        <p>
          This page is for customer accounts only. Provider, admin, and support accounts are
          managed through a separate offboarding process to make sure venues, payouts, and team
          access are handled safely.
        </p>
        <p>
          If you need to close a business or operator account, please contact us at{" "}
          <a href="mailto:support@gaamed.com">support@gaamed.com</a>.
        </p>
      </LegalPage>
    );
  }

  return (
    <LegalPage
      eyebrow="Account"
      lastUpdated="July 2026"
      title="Delete your Gaamed account"
    >
      <p>
        When we delete your account, the following data will be permanently removed from our
        live systems:
      </p>
      <ul>
        <li>Your Gaamed account, profile, email, and password.</li>
        <li>Saved venues and preferences.</li>
        <li>Password-reset tokens and email-verification codes.</li>
        <li>
          The comment text on any venue reviews you left (star ratings are kept, but they are
          no longer linked to your identity).
        </li>
        <li>
          Your personal information in booking and session records, including your name, email,
          and phone number.
        </li>
      </ul>

      <p>
        Some records must be kept for legal, tax, fraud-prevention, and dispute-resolution
        purposes. These are anonymized and no longer identify you:
      </p>
      <ul>
        <li>
          Receipts and payment transactions (amounts, method, timestamps, venue, and provider) are
          kept for the period required by applicable tax law, usually up to five years in
          Egypt.
        </li>
        <li>
          Booking and session metadata (venue, unit, timing, and financial data) are kept for
          dispute resolution and platform security.
        </li>
        <li>
          A minimal account-deletion audit log is kept to record that the request was made,
          reviewed, and completed.
        </li>
      </ul>

      <p>
        Deletion is not instant. We review requests to confirm there are no active bookings,
        sessions, or pending food orders. We aim to complete this review within 30 days.
      </p>

      {loadError ? (
        <div className="rounded-xl border border-rose-400/20 bg-rose-400/10 p-4 text-[var(--color-text)]" role="alert">
          <p>{loadError}</p>
          <button className="ops-button mt-3" onClick={() => setLoadAttempt((attempt) => attempt + 1)} type="button">Retry checking active items</button>
        </div>
      ) : null}

      {isLoading ? (
        <p className="text-sm text-slate-400">Checking your active bookings and sessions...</p>
      ) : hasActiveItems ? (
        <ActiveItemList
          bookings={bookings}
          foodOrders={foodOrders}
          sessions={sessions}
        />
      ) : null}

      <label className="block">
        <span className="mb-1.5 block text-sm text-slate-300">
          Reason for deletion (optional)
        </span>
        <textarea
          className="ops-input min-h-[120px] w-full"
          onChange={(event) => setReason(event.target.value)}
          placeholder="Help us improve by telling us why you are leaving..."
          rows={4}
          value={reason}
        />
      </label>

      <label className="flex items-start gap-3">
        <input
          checked={confirmed}
          className="mt-1 size-4 rounded border-white/20"
          onChange={(event) => setConfirmed(event.target.checked)}
          type="checkbox"
        />
        <span className="text-sm text-slate-300">
          I understand this will permanently delete my account and personal data, and that this
          action cannot be undone.
        </span>
      </label>

      {submitError ? (
        <div className="rounded-xl border border-rose-400/20 bg-rose-400/10 p-4 text-[var(--color-text)]" role="alert">
          {submitError}
        </div>
      ) : null}

      <div className="pt-2">
        <button
          className="ops-button"
          data-variant="danger"
          disabled={!confirmed || submitting || isLoading || !!loadError || hasActiveItems}
          onClick={() => void handleSubmit()}
          type="button"
        >
          {submitting ? "Submitting request..." : "Request account deletion"}
        </button>
      </div>
    </LegalPage>
  );
}
